
  import { computed, defineComponent, onBeforeUnmount, onMounted, ref } from 'vue';
  import SplitButton from '@/components/buttons/SplitButton.vue';
  import Loading from '@/components/animation/Loading.vue';
  import { Button, generateCheckClickOutside, Hint, Icon, Typography } from '@voximplant/spaceui';
  import DropdownList from '@/components/common/DropdownList.vue';
  import { useStore } from 'effector-vue/composition';
  import { $settings, addActiveAudioDevice, changeMute } from '@/store/settings';
  import {
    $calls,
    changeAudioDevice,
    currentSelectCall,
    hangUp,
    toggleCallStatus,
  } from '@/store/calls';
  import { changeComponent, changeComponentDialingStatus } from '@/store/components';
  import { needToShowAdditionalButton } from '@/store/NeedToShowAdditionalButton';
  import { useI18n } from 'vue-i18n';
  import CallInfo from '@/components/calls/CallInfo.vue';
  import TransferCallList from '@/components/calls/TransferCallList.vue';

  export default defineComponent({
    name: 'AudioCallFooter',
    components: {
      SplitButton,
      Loading,
      Typography,
      Button,
      Icon,
      Hint,
      DropdownList,
      CallInfo,
      TransferCallList,
    },
    setup() {
      const { t } = useI18n();
      const isOpenMicList = ref(false);
      const isOpenTransferSelect = ref(false);
      const selectCalls = ref<HTMLDivElement | null>(null);
      const selectMics = ref<HTMLDivElement | null>(null);
      const settingsState = useStore($settings);
      const selectCall = useStore(currentSelectCall);
      const calls = useStore($calls);
      const showAdditionalButton = useStore(needToShowAdditionalButton);
      const isCallPaused = computed(() => !selectCall.value?.active);
      const isCallProgressing = computed(
        () => selectCall.value?.status === 'PROGRESSING' || selectCall.value?.status === 'ALERTING'
      );
      const canCurrentCallToggleActive = computed(() => {
        if (!selectCall.value) return false;
        return calls.value[selectCall.value?.id]?.params.canToggleActive;
      });
      const getIconColor = (disabled: boolean) => {
        if (disabled) return '--sui-gray-400';
        else return settingsState.value.mute ? '--sui-red-500' : '--sui-gray-700';
      };

      const checkClickOutsideCalls = generateCheckClickOutside(selectCalls);
      const checkClickOutsideMics = generateCheckClickOutside(selectMics);
      const onOpenTransferSelect = () => (isOpenTransferSelect.value = !isOpenTransferSelect.value);
      const onOpenListMics = () => (isOpenMicList.value = !isOpenMicList.value);

      const onClickOutside = (event: MouseEvent) => {
        if (checkClickOutsideCalls(event) && isOpenTransferSelect.value) onOpenTransferSelect();
        if (checkClickOutsideMics(event) && isOpenMicList.value) onOpenListMics();
      };
      const closeSelect = (event: KeyboardEvent) => {
        const isPushKey = event.key === 'Escape';
        if (isPushKey && isOpenTransferSelect.value) isOpenTransferSelect.value = false;
        if (isPushKey && isOpenMicList.value) isOpenMicList.value = false;
      };
      const isAudioActive = (id: number | string) => {
        return id === settingsState.value.activeAudioDevice.value;
      };
      const setActiveAudioDevice = (name: string, id: string) => {
        if (!settingsState.value.mute) {
          addActiveAudioDevice({ label: name, value: id });
          changeAudioDevice(id);
        } else {
          addActiveAudioDevice({ label: name, value: id });
        }
      };
      const makeNewCall = () => {
        changeComponent('Dialing');
        changeComponentDialingStatus('newCall');
      };
      const switchNumpad = () => {
        changeComponent('Dialing');
        changeComponentDialingStatus('toneDial');
      };

      onMounted(() => {
        document.addEventListener('click', onClickOutside, { capture: true });
        document.addEventListener('keydown', closeSelect);
      });

      onBeforeUnmount(() => {
        document.removeEventListener('click', onClickOutside);
        document.removeEventListener('keydown', closeSelect);
      });

      return {
        t,
        calls,
        settingsState,
        selectCall,
        selectCalls,
        selectMics,
        isOpenMicList,
        isOpenTransferSelect,
        onOpenTransferSelect,
        isCallPaused,
        isCallProgressing,
        canCurrentCallToggleActive,
        showAdditionalButton,
        changeMute,
        hangUp,
        isAudioActive,
        setActiveAudioDevice,
        makeNewCall,
        onOpenListMics,
        switchNumpad,
        toggleCallStatus,
        getIconColor,
      };
    },
  });
